// The unified Inbox's decisions as pure functions (Vitest's node environment covers them): thread
// keys, the WhatsApp 24h window line, the URL state (with the legacy deep links), the channel tabs
// and the live merge of a `wa-thread-update` into the loaded rows.
//
// The window line is display only: the composer (Task 18) re-checks the window on every tick and
// the server's 409 is the source of truth for sending.
import type { LocationQueryRaw } from 'vue-router'
import type { EngageChannel } from '@/lib/engageGate'
import type { InboxChannel, InboxCounts, InboxEmailStatus, InboxFilter, InboxThreadRow, InboxThreadsParams } from '@/lib/inbox-api'
import type { WaThreadUpdatePayload } from '@/types/whatsapp'

// ── thread keys ─────────────────────────────────────────────────────────

export type ThreadKind = 'wa' | 'em' | 'emu'

export interface ThreadKey {
  channel: EngageChannel
  kind: ThreadKind
  id: string
}

const KEY_RE = /^(wa|em|emu):([0-9a-fA-F]{24})$/
const KIND_CHANNEL: Record<ThreadKind, EngageChannel> = { wa: 'whatsapp', em: 'email', emu: 'email' }

/** `wa:<conversationId>`, `em:<enrollmentId>` or `emu:<emailMessageId>` (unmatched email); null for anything else. */
export function parseThreadKey(key: unknown): ThreadKey | null {
  if (typeof key !== 'string') return null
  const m = KEY_RE.exec(key)
  if (!m) return null
  const kind = m[1] as ThreadKind
  return { channel: KIND_CHANNEL[kind], kind, id: m[2] as string }
}

const isObjectId = (v: string): boolean => /^[0-9a-fA-F]{24}$/.test(v)

// ── the 24h window ──────────────────────────────────────────────────────

/** The window line turns amber at this much time left (ruling 8). */
export const WINDOW_CLOSING_MS = 4 * 3_600_000
/** How often window lines re-render (the old WhatsApp inbox's countdown tick). */
export const WINDOW_TICK_MS = 15_000

const HOUR = 3_600_000
const MINUTE = 60_000

export type WindowStateName = 'open' | 'closing' | 'closed' | 'none'

type WindowRow = Pick<InboxThreadRow, 'channel' | 'windowExpiresAt'>

const epoch = (now: number | Date): number => (now instanceof Date ? now.getTime() : now)

/**
 * Where a thread's free-reply window stands at `now`: `closing` once 4 h or less are left, `closed`
 * when it has expired (or never opened), `none` for email (no window at all). `msLeft` is the time
 * left, 0 once expired, null when there is no window to measure.
 */
export function windowState(row: WindowRow, now: number | Date): { state: WindowStateName; msLeft: number | null } {
  if (row.channel !== 'whatsapp') return { state: 'none', msLeft: null }
  const expires = row.windowExpiresAt ? Date.parse(row.windowExpiresAt) : NaN
  if (!Number.isFinite(expires)) return { state: 'closed', msLeft: null }
  const left = expires - epoch(now)
  if (left <= 0) return { state: 'closed', msLeft: 0 }
  return { state: left <= WINDOW_CLOSING_MS ? 'closing' : 'open', msLeft: left }
}

export type WindowTone = 'ok' | 'soon' | 'closed' | 'email'

/** The line under a thread row: what the viewer may send now, in the board's words. */
export function windowLine(row: WindowRow, now: number | Date): { text: string; tone: WindowTone } {
  const { state, msLeft } = windowState(row, now)
  if (state === 'none') return { text: 'Email · no time limit', tone: 'email' }
  if (state === 'closed' || msLeft === null) return { text: 'Window closed · template only', tone: 'closed' }
  // Floored, so the line never promises more time than is left.
  const hours = Math.floor(msLeft / HOUR)
  if (state === 'open') return { text: `Window open · ${hours}h left`, tone: 'ok' }
  if (hours >= 1) return { text: `Window closes in ${hours}h`, tone: 'soon' }
  return { text: `Window closes in ${Math.max(1, Math.floor(msLeft / MINUTE))} min`, tone: 'soon' }
}

// ── URL state ───────────────────────────────────────────────────────────

/** Email statuses the Status select offers (the old email inbox's tabs). */
export const EMAIL_STATUS_OPTIONS: readonly { value: InboxEmailStatus; label: string }[] = [
  { value: 'replied', label: 'Replied' },
  { value: 'sent', label: 'Sent' },
  { value: 'opened', label: 'Opened' },
  { value: 'bounced', label: 'Bounced' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
  { value: 'send_failed', label: 'Failed' },
]

/**
 * An email enrollment status in words: the Status select's label (send_failed is "Failed"), else
 * the status in Title Case (Pending, Completed, ...); '' when there is none (WhatsApp, unmatched).
 */
export function emailStatusLabel(status: string | null | undefined): string {
  if (!status) return ''
  const option = EMAIL_STATUS_OPTIONS.find((o) => o.value === status)
  return option ? option.label : status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

/** Email statuses that mean a reply will not get through: the list rows carry a tag for these. */
const EMAIL_PROBLEM_STATUSES: readonly string[] = ['bounced', 'unsubscribed', 'send_failed']

/** The tag an email row carries for a problem status (Bounced, Unsubscribed, Failed); null otherwise. */
export function emailProblemStatus(row: Pick<InboxThreadRow, 'channel' | 'status'>): { status: string; label: string } | null {
  if (row.channel !== 'email' || !row.status || !EMAIL_PROBLEM_STATUSES.includes(row.status)) return null
  return { status: row.status, label: emailStatusLabel(row.status) }
}

export interface InboxState {
  channel: InboxChannel
  filter: InboxFilter
  /** Email enrollment status; only on the Email tab, '' = any. */
  status: InboxEmailStatus | ''
  /** Contact filter (the legacy `?contactId=` deep link); '' = none. */
  contactId: string
  /** WhatsApp sender number; '' = all numbers. */
  senderId: string
  /** Open thread key (`wa:` / `em:` / `emu:`); '' = none. */
  thread: string
}

export function emptyInboxState(): InboxState {
  return { channel: 'all', filter: 'all', status: '', contactId: '', senderId: '', thread: '' }
}

/** Keys the inbox owns in its URL; everything else (`?org=`) is left alone. `enrollment` is the
 *  legacy email deep link, read but never written. */
export const INBOX_QUERY_KEYS = ['channel', 'filter', 'status', 'contactId', 'senderId', 'thread', 'enrollment'] as const

type QueryLike = Record<string, unknown>

function firstString(v: unknown): string {
  const value = Array.isArray(v) ? v[0] : v
  return typeof value === 'string' ? value : ''
}

const CHANNELS: readonly InboxChannel[] = ['all', 'whatsapp', 'email']
const FILTERS: readonly InboxFilter[] = ['all', 'needs_reply', 'unmatched']

/**
 * The state a URL asks for. Unknown values fall back to the defaults; the legacy
 * `?enrollment=<id>` opens `em:<id>` (an explicit `thread` wins); a status implies the Email tab
 * (and is dropped on the WhatsApp tab and the unmatched filter, where no row has one).
 */
export function stateFromQuery(q: QueryLike): InboxState {
  const s = emptyInboxState()
  const channel = firstString(q.channel)
  if ((CHANNELS as readonly string[]).includes(channel)) s.channel = channel as InboxChannel
  const filter = firstString(q.filter)
  if ((FILTERS as readonly string[]).includes(filter)) s.filter = filter as InboxFilter

  const status = firstString(q.status)
  if (EMAIL_STATUS_OPTIONS.some((o) => o.value === status) && s.channel !== 'whatsapp' && s.filter !== 'unmatched') {
    s.status = status as InboxEmailStatus
    s.channel = 'email'
  }

  const contactId = firstString(q.contactId)
  if (isObjectId(contactId)) s.contactId = contactId
  const senderId = firstString(q.senderId)
  if (isObjectId(senderId)) s.senderId = senderId

  const thread = firstString(q.thread)
  const enrollment = firstString(q.enrollment)
  if (parseThreadKey(thread)) s.thread = thread
  else if (isObjectId(enrollment)) s.thread = `em:${enrollment}`
  return s
}

/** The inbox's own keys for a state; defaults are left out, so the plain inbox is the bare URL. */
export function toQuery(state: InboxState): LocationQueryRaw {
  const q: LocationQueryRaw = {}
  if (state.channel !== 'all') q.channel = state.channel
  if (state.filter !== 'all') q.filter = state.filter
  if (state.status) q.status = state.status
  if (state.contactId) q.contactId = state.contactId
  if (state.senderId) q.senderId = state.senderId
  if (state.thread) q.thread = state.thread
  return q
}

/** `base` (the current query) with the inbox's own keys replaced by the state's. */
export function mergeQuery(base: QueryLike, state: InboxState): LocationQueryRaw {
  const next: LocationQueryRaw = { ...(base as LocationQueryRaw) }
  for (const key of INBOX_QUERY_KEYS) delete next[key]
  return { ...next, ...toQuery(state) }
}

export interface InboxViewer {
  /** What `inboxChannels()` gives this viewer (WhatsApp first). */
  channels: EngageChannel[]
  isAdmin: boolean
}

/**
 * The state narrowed to what this viewer can see: a single channel is always the selected tab (it
 * has no All tab); a channel, thread or sender the viewer has no access to is dropped; unmatched is
 * admin only; a status needs the Email tab and is meaningless on the unmatched filter.
 */
export function fitState(state: InboxState, viewer: InboxViewer): InboxState {
  const s = { ...state }
  const { channels } = viewer
  if (channels.length === 1) s.channel = channels[0] as EngageChannel
  else if (s.channel !== 'all' && !channels.includes(s.channel)) s.channel = 'all'
  if (s.filter === 'unmatched' && !viewer.isAdmin) s.filter = 'all'
  if (s.channel !== 'email' || s.filter === 'unmatched') s.status = ''
  if (!channels.includes('whatsapp')) s.senderId = ''
  const key = parseThreadKey(s.thread)
  if (s.thread && (!key || !channels.includes(key.channel))) s.thread = ''
  return s
}

/** Rows per page (the API's default). */
export const INBOX_PAGE_SIZE = 30

/** API params for the list the state shows (never the open thread: opening one keeps the list). */
export function listParams(state: InboxState, cursor?: string | null): InboxThreadsParams {
  const p: InboxThreadsParams = {}
  if (state.channel !== 'all') p.channel = state.channel
  if (state.filter !== 'all') p.filter = state.filter
  if (state.status) p.status = state.status
  if (state.contactId) p.contactId = state.contactId
  if (state.senderId) p.senderId = state.senderId
  if (cursor) p.cursor = cursor
  p.limit = INBOX_PAGE_SIZE
  return p
}

/** Changes exactly when the list must be fetched again. */
export function listKey(state: InboxState): string {
  return JSON.stringify(listParams(state))
}

/** Whether a realtime event on `channel` can change the list the state shows. */
export function listShowsChannel(state: InboxState, channel: EngageChannel): boolean {
  if (channel === 'whatsapp') return state.channel !== 'email' && !state.status
  return state.channel !== 'whatsapp'
}

/**
 * The thread a `?contactId=` deep link opens once that contact's list has loaded: the newest
 * (first, the list is newest first) row of that contact, and only while no thread is open; null
 * otherwise (nothing to open, or rows that are not that contact's list).
 */
export function autoOpenThread(
  rows: readonly Pick<InboxThreadRow, 'key' | 'contactId'>[],
  contactId: string,
  openThread: string,
): string | null {
  if (!contactId || openThread) return null
  return rows.find((r) => r.contactId === contactId)?.key ?? null
}

// ── tabs ────────────────────────────────────────────────────────────────

export interface InboxTab {
  value: InboxChannel
  label: string
  /** Threads waiting for a reply; null until the counts load. */
  count: number | null
}

const CHANNEL_TAB_LABEL: Record<EngageChannel, string> = { whatsapp: 'WhatsApp', email: 'Email' }

/** The channel tablist: All plus each available channel, or just the one channel when there is one. */
export function tabsFor(channels: readonly EngageChannel[], counts: InboxCounts | null): InboxTab[] {
  const channelTabs = channels.map((c): InboxTab => ({ value: c, label: CHANNEL_TAB_LABEL[c], count: counts ? counts[c] : null }))
  if (channelTabs.length < 2) return channelTabs
  return [{ value: 'all', label: 'All', count: counts ? counts.all : null }, ...channelTabs]
}

// ── realtime ────────────────────────────────────────────────────────────

const WINDOW_MS = 24 * HOUR

/**
 * A `wa-thread-update` merged into the loaded rows (pure: changed rows are copies). A new message
 * updates its row's snippet, time and direction, moves it to the top (the list is newest first)
 * and, when inbound, makes it wait for a reply, counts it unread unless it is the open thread
 * (`openKey`), and reopens the 24h window as the server does. Status and media patches change no
 * row. A message for a thread the list does not hold asks for a `refetch` (it may belong on it).
 */
export function applyRealtime(
  rows: InboxThreadRow[],
  event: WaThreadUpdatePayload,
  opts: { openKey?: string | null } = {},
): { rows: InboxThreadRow[]; refetch: boolean } {
  const { message } = event
  if (!message) return { rows, refetch: false }
  const key = `wa:${event.conversationId}`
  const index = rows.findIndex((r) => r.key === key)
  const current = rows[index]
  if (!current) return { rows, refetch: true }

  const inbound = message.direction === 'inbound'
  const sentAt = Date.parse(message.createdAt)
  const updated: InboxThreadRow = {
    ...current,
    snippet: message.body,
    lastMessageAt: message.createdAt,
    lastDirection: message.direction,
    needsReply: inbound,
  }
  if (inbound) {
    if (opts.openKey !== key) updated.unreadCount = (current.unreadCount || 0) + 1
    if (Number.isFinite(sentAt)) updated.windowExpiresAt = new Date(sentAt + WINDOW_MS).toISOString()
  }
  const next = rows.filter((_, i) => i !== index)
  next.unshift(updated)
  return { rows: next, refetch: false }
}

// ── row presentation ────────────────────────────────────────────────────

/** Bold rows (R25): WhatsApp with unread messages, email waiting for a reply. */
export function isBold(row: Pick<InboxThreadRow, 'channel' | 'unreadCount' | 'needsReply'>): boolean {
  return row.channel === 'whatsapp' ? row.unreadCount > 0 : row.needsReply
}

export function rowName(row: Pick<InboxThreadRow, 'contact' | 'address'>): string {
  return row.contact?.name?.trim() || row.address?.trim() || 'Unknown sender'
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const pad = (n: number) => String(n).padStart(2, '0')
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()

/** A row's time in local time: `21:05` today, the weekday within the last six days, `Oct 17` this
 *  year, `Dec 31, 2025` before; '' when there is none. */
export function rowTime(iso: string | null, now: Date = new Date()): string {
  const ms = iso ? Date.parse(iso) : NaN
  if (!Number.isFinite(ms)) return ''
  const d = new Date(ms)
  const days = Math.round((startOfDay(now) - startOfDay(d)) / (24 * HOUR))
  if (days === 0) return `${pad(d.getHours())}:${pad(d.getMinutes())}`
  if (days > 0 && days < 7) return WEEKDAYS[d.getDay()] as string
  const date = `${MONTHS[d.getMonth()]} ${d.getDate()}`
  return d.getFullYear() === now.getFullYear() ? date : `${date}, ${d.getFullYear()}`
}

/** What an empty list says, by what narrows it. */
export function emptyText(state: InboxState): string {
  if (state.filter === 'unmatched') return 'No unmatched conversations.'
  if (state.filter === 'needs_reply') return 'Nothing is waiting for a reply.'
  if (state.contactId) return 'No conversations with this contact.'
  if (state.status) return 'No email threads with this status.'
  return 'No conversations yet.'
}
